/**
 * Deck-template tools — "save this deck in Usable" / "start from template X".
 *
 * Architecture (user decision, 2026-07-02): templates are LEAN — the deck
 * CONFIGURATION lives as spec JSON in a Usable fragment's content, and only
 * the image assets are stored as attached files. No whole-PPTX blobs.
 *
 * Division of labour: these tools produce ground truth (geometry, text,
 * colors, image provenance) and move bytes; the MODEL authors the fragment
 * (spec + human summary + `deck-template` tag + attached assets) with its own
 * Usable tools, and restores by REPLAYING the spec through the existing
 * tools (set_theme_colors → apply_master_branding → compose_deck /
 * primitives) — there is deliberately no separate restore machinery.
 *
 * Office.js cannot extract image bytes from shapes, so assets come from the
 * image-provenance registry (recorded at insert time). Durable sources
 * (workspace fileIds, URLs) are stored as references; ephemeral ones are
 * uploaded via the durable-upload bridge (board task 09120133).
 */

import type { PptxTool } from "../tool-types";
import {
  listImageProvenance,
  isDurableSource,
  type ImageProvenance,
} from "../image-provenance";
import { getWorkspaceFilesApi } from "../staged-data";

/** Stable asset key for one placed image. */
export function assetKey(entry: Pick<ImageProvenance, "scope" | "shapeId">): string {
  return `img_${entry.scope}_${entry.shapeId}`;
}

export interface DeckSpecShape {
  id: string;
  name: string;
  type: string;
  rect: { left: number; top: number; width: number; height: number };
  text?: string;
  font?: { size?: number; name?: string; color?: string; bold?: boolean };
  fillColor?: string;
  /** Present when this shape is a picture fill we placed (see assets). */
  imageAssetKey?: string;
}

export interface DeckSpecAsset {
  key: string;
  scope: number | "master";
  shapeId: string;
  source: string;
  /** fileId / URL — restore address for durable sources; set post-upload otherwise. */
  ref?: string;
  /** True until upload_template_assets stores the bytes durably. */
  needsUpload: boolean;
  /** Bytes are held in the session registry (uploadable). */
  bytesHeld: boolean;
}

/** Pure spec assembly — unit-testable without Office. */
export function buildDeckSpec(
  slides: Array<{ index: number; shapes: DeckSpecShape[] }>,
  themeColors: Record<string, string> | undefined,
  provenance: ImageProvenance[]
): {
  version: 1;
  canvas: { width: number; height: number };
  themeColors?: Record<string, string>;
  master?: { images: DeckSpecAsset[] };
  slides: Array<{ index: number; shapes: DeckSpecShape[] }>;
  assets: DeckSpecAsset[];
  warnings: string[];
} {
  const warnings: string[] = [];
  const assets: DeckSpecAsset[] = provenance.map((p) => ({
    key: assetKey(p),
    scope: p.scope,
    shapeId: p.shapeId,
    source: p.source,
    ref: isDurableSource(p.source) ? p.ref : undefined,
    needsUpload: !isDurableSource(p.source),
    bytesHeld: Boolean(p.base64),
  }));

  // Tag picture shapes with their asset key; flag picture fills we can't source.
  const byScopeShape = new Map(assets.map((a) => [`${a.scope}:${a.shapeId}`, a]));
  for (const slide of slides) {
    for (const shape of slide.shapes) {
      const asset = byScopeShape.get(`${slide.index}:${shape.id}`);
      if (asset) shape.imageAssetKey = asset.key;
      else if (shape.fillColor === undefined && shape.type === "GeometricShape" && !shape.text) {
        // Likely a picture fill with no provenance (placed outside this
        // session / by hand) — the spec cannot restore its image.
        warnings.push(
          `Slide ${slide.index}, shape ${shape.id}: possible image with unknown source — ` +
            `it will not restore. Re-insert it via insert_image to capture provenance, or note it in the fragment.`
        );
      }
    }
  }
  const unuploadable = assets.filter((a) => a.needsUpload && !a.bytesHeld);
  for (const a of unuploadable) {
    warnings.push(
      `Asset ${a.key} (${a.source}) has no bytes in the session registry — re-insert that image, then export again.`
    );
  }

  const masterImages = assets.filter((a) => a.scope === "master");
  return {
    version: 1,
    canvas: { width: 960, height: 540 },
    ...(themeColors ? { themeColors } : {}),
    ...(masterImages.length > 0 ? { master: { images: masterImages } } : {}),
    slides,
    assets,
    warnings,
  };
}

const THEME_SLOTS: Record<string, string> = {
  dark1: "Dark1", dark2: "Dark2", light1: "Light1", light2: "Light2",
  accent1: "Accent1", accent2: "Accent2", accent3: "Accent3",
  accent4: "Accent4", accent5: "Accent5", accent6: "Accent6",
  hyperlink: "Hyperlink", followedHyperlink: "FollowedHyperlink",
};

export const templateTools: PptxTool[] = [
  // ─── Export Deck Spec ─────────────────────────────────────────────────────

  {
    schema: {
      name: "export_deck_spec",
      description:
        "SAVE-AS-TEMPLATE step 1: capture this deck's full configuration as a spec — every " +
        "shape's geometry/text/font/fill per slide, theme colors, and the provenance of every " +
        "image placed this session (durable refs vs assets needing upload). Then: (2) call " +
        "upload_template_assets for any needsUpload assets, (3) create a Usable fragment " +
        "yourself — spec as fenced JSON in the content plus a human summary, tagged " +
        "'deck-template', with the uploaded files attached. RESTORE: read such a fragment and " +
        "REPLAY it — set_theme_colors, apply_master_branding (image refs by fileId), then " +
        "compose_deck or the shape primitives with useUploadedImage: <fileId>. Decks built " +
        "with compose tools round-trip exactly; hand-made content is captured best-effort " +
        "(check warnings).",
      parameters: {
        type: "object",
        properties: {
          slideIndexes: {
            type: "array",
            description: "Optional subset of 0-based slide indexes (default: every slide).",
            items: { type: "number" },
          },
        },
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const targets =
          Array.isArray(args.slideIndexes) && (args.slideIndexes as number[]).length > 0
            ? (args.slideIndexes as number[])
            : slides.items.map((_, i) => i);
        const invalid = targets.filter((i) => i < 0 || i >= slides.items.length);
        if (invalid.length > 0) {
          throw new Error(
            `slideIndexes out of range: ${invalid.join(", ")} (presentation has ${slides.items.length} slides).`
          );
        }

        // Load every target slide's shape surface in one batch (same load
        // strings as get_slide — proven readable).
        const perSlide = targets.map((index) => {
          const shapes = slides.items[index].shapes;
          shapes.load(
            "items/id,items/name,items/type,items/left,items/top,items/width,items/height," +
              "items/fill/type,items/fill/foregroundColor"
          );
          return { index, shapes };
        });
        await context.sync();

        const textFrames = perSlide.map(({ shapes }) =>
          shapes.items.map((shape) => {
            const tf = shape.textFrame;
            tf.load("hasText");
            tf.textRange.load("text,font/size,font/name,font/color,font/bold");
            return tf;
          })
        );
        await context.sync();

        const slidesData = perSlide.map(({ index, shapes }, si) => ({
          index,
          shapes: shapes.items.map((shape, shi): DeckSpecShape => {
            const tf = textFrames[si][shi];
            const hasText = (() => {
              try {
                return tf.hasText && tf.textRange.text.trim().length > 0;
              } catch {
                return false;
              }
            })();
            const fillType = String(shape.fill.type ?? "");
            return {
              id: shape.id,
              name: shape.name,
              type: String(shape.type),
              rect: { left: shape.left, top: shape.top, width: shape.width, height: shape.height },
              ...(hasText
                ? {
                    text: tf.textRange.text,
                    font: {
                      size: tf.textRange.font.size ?? undefined,
                      name: tf.textRange.font.name ?? undefined,
                      color: tf.textRange.font.color ?? undefined,
                      bold: tf.textRange.font.bold ?? undefined,
                    },
                  }
                : {}),
              ...(fillType.toLowerCase() === "solid" && shape.fill.foregroundColor
                ? { fillColor: shape.fill.foregroundColor }
                : {}),
            };
          }),
        }));

        // Theme colors are PowerPointApi 1.10 — probe gracefully.
        let themeColors: Record<string, string> | undefined;
        try {
          const masters = context.presentation.slideMasters;
          masters.load("items");
          await context.sync();
          const scheme = masters.items[0]?.themeColorScheme;
          if (scheme) {
            const reads: Array<[string, OfficeExtension.ClientResult<string>]> = Object.entries(
              THEME_SLOTS
            ).map(([key, slot]) => [key, scheme.getThemeColor(slot as unknown as PowerPoint.ThemeColor)]);
            await context.sync();
            themeColors = {};
            for (const [key, result] of reads) themeColors[key] = result.value;
          }
        } catch {
          themeColors = undefined;
        }

        const spec = buildDeckSpec(slidesData, themeColors, listImageProvenance());
        const needsUpload = spec.assets.filter((a) => a.needsUpload && a.bytesHeld);
        return {
          spec,
          nextStep:
            needsUpload.length > 0
              ? `Call upload_template_assets (${needsUpload.length} asset(s) need durable storage), merge the returned fileIds into the spec's assets[].ref, THEN create the fragment.`
              : "All image assets are durable references — create the Usable fragment now (spec as fenced JSON + summary, tag 'deck-template', attach a preview if desired).",
        };
      });
    },
  },

  // ─── Upload Template Assets ───────────────────────────────────────────────

  {
    minRequirementSet: "1.8", // preview screenshot uses getImageAsBase64
    schema: {
      name: "upload_template_assets",
      description:
        "SAVE-AS-TEMPLATE step 2: durably store the image assets that export_deck_spec flagged " +
        "needsUpload (their bytes are held from this session's inserts), plus a slide preview " +
        "screenshot. Returns key→fileId — merge these into the spec's assets[].ref, then create " +
        "the template fragment and ATTACH these fileIds to it. Requires the durable-upload " +
        "bridge on the embed config; if it errors as unsupported, say so — the platform side " +
        "may not be released yet (board task 09120133).",
      parameters: {
        type: "object",
        properties: {
          assetKeys: {
            type: "array",
            description: "Asset keys to upload (default: everything flagged needsUpload with bytes held).",
            items: { type: "string" },
          },
          templateName: {
            type: "string",
            description: "Template name used in stored file names (kebab-cased).",
          },
          previewSlideIndex: {
            type: "number",
            description: "0-based slide to screenshot as the preview (default 0; -1 to skip).",
          },
        },
      },
    },
    handler: async (args) => {
      const api = getWorkspaceFilesApi();
      if (!api) {
        throw new Error("Workspace-file access is not available (embed config gate off).");
      }
      const name = (typeof args.templateName === "string" && args.templateName.length > 0
        ? (args.templateName as string)
        : "deck-template"
      )
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

      const provenance = listImageProvenance();
      const wanted = Array.isArray(args.assetKeys) && (args.assetKeys as string[]).length > 0
        ? new Set(args.assetKeys as string[])
        : null;
      const uploadable = provenance.filter(
        (p) => p.base64 && !isDurableSource(p.source) && (!wanted || wanted.has(assetKey(p)))
      );

      const uploaded: Record<string, string> = {};
      const failures: string[] = [];
      for (const entry of uploadable) {
        const key = assetKey(entry);
        try {
          const { fileId } = await api.putBytes(`${name}-${key}.png`, "image/png", entry.base64!);
          uploaded[key] = fileId;
        } catch (err) {
          failures.push(`${key}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }

      // Preview screenshot (best-effort — a template without a preview is fine).
      let previewFileId: string | undefined;
      const previewIndex =
        typeof args.previewSlideIndex === "number" ? (args.previewSlideIndex as number) : 0;
      if (previewIndex >= 0 && failures.length === 0) {
        try {
          const base64 = await PowerPoint.run(async (context) => {
            const slide = context.presentation.slides.getItemAt(previewIndex);
            const img = slide.getImageAsBase64({ width: 800 });
            await context.sync();
            return img.value.replace(/\s+/g, "");
          });
          const { fileId } = await api.putBytes(`${name}-preview.png`, "image/png", base64);
          previewFileId = fileId;
        } catch (err) {
          failures.push(`preview: ${err instanceof Error ? err.message : String(err)}`);
        }
      }

      return {
        uploaded,
        ...(previewFileId ? { previewFileId } : {}),
        ...(failures.length > 0 ? { failures } : {}),
        message:
          Object.keys(uploaded).length > 0 || previewFileId
            ? "Merge these fileIds into the spec (assets[].ref by key), then create the Usable fragment (spec + summary, tag 'deck-template') and ATTACH these files to it."
            : failures.length > 0
              ? "Nothing was stored — see failures. If the error says the request is unsupported, the durable-upload bridge is not live yet: save the spec fragment with durable refs only and note the pending assets."
              : "Nothing needed uploading — all assets are already durable references.",
      };
    },
  },
];
